import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import GalleryLightbox from '../components/GalleryLightbox'
import ContactForm from '../components/ContactForm'
import { getResizedImageUrl } from '../lib/utils'

const FALLBACK_HERO_IMAGES = [
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7688-1024x681.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7669-jpg.webp',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-5926-1024x681.jpg'
]

export default function PageDetail() {
  const { slug, subSlug } = useParams()
  const navigate = useNavigate()

  // Extract slug from pathname if not provided by params
  const getSlugFromPath = () => {
    if (subSlug) return subSlug
    if (slug) return slug
    
    // Fallback to parsing pathname
    const pathParts = window.location.pathname.split('/').filter(Boolean)
    if (pathParts.length > 0) {
      return pathParts[pathParts.length - 1]
    }
    return 'index'
  }

  const currentSlug = getSlugFromPath()

  const [pageData, setPageData] = useState(null)
  const [galleryData, setGalleryData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0)

  // Technical reference mapping page slug -> gallery slug
  const galleryMapping = {
    'landbrugsmaskinstation': 'landbrugsmaskinstation-galleri',
    'entreprenoer-og-anlaeg': 'entreprenoer-og-anlaeg-galleri',
    'maskinpark': 'maskinpark-galleri',
    'sand-sten-grus': 'sand-sten-og-grus-galleri',
    'historie': 'historie-galleri'
  }

  const heroImagesToUse = pageData?.images && pageData.images.length > 0
    ? pageData.images
    : FALLBACK_HERO_IMAGES

  useEffect(() => {
    setCurrentHeroIndex(0)
  }, [currentSlug])

  useEffect(() => {
    if (heroImagesToUse.length > 1) {
      const interval = setInterval(() => {
        setCurrentHeroIndex((prev) => (prev + 1) % heroImagesToUse.length)
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [heroImagesToUse])

  const getBadgeText = () => {
    if (pageData?.parent_slug) {
      const formatted = pageData.parent_slug
        .replace(/-/g, ' ')
        .replace(/oe/g, 'ø')
        .replace(/ae/g, 'æ')
        .replace(/aa/g, 'å')
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    }
    return 'Kvalitet er en hobby'
  }

  useEffect(() => {
    const fetchPageAndGallery = async () => {
      setLoading(true)
      setError(null)
      setGalleryData(null)

      try {
        // 1. Fetch Page Content
        const pageRes = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${currentSlug}`)
        if (!pageRes.ok) {
          if (pageRes.status === 404) {
            navigate('/404', { replace: true })
            return
          }
          throw new Error('Kunne ikke hente sideindhold')
        }
        const pageJson = await pageRes.json()
        setPageData(pageJson)

        // Update document metadata
        document.title = pageJson.meta_title || `${pageJson.title} - Sloth’s Maskinstation`
        const metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) {
          metaDesc.setAttribute('content', pageJson.meta_description || '')
        }

        // 2. Fetch Gallery if mapped
        const gallerySlug = galleryMapping[currentSlug]
        if (gallerySlug) {
          const galleryRes = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${gallerySlug}`)
          if (galleryRes.ok) {
            const galleryJson = await galleryRes.json()
            setGalleryData(galleryJson)
          }
        }
      } catch (err) {
        console.error(err)
        setError('Der skete en fejl under indlæsning af siden. Prøv venligst igen.')
      } finally {
        setLoading(false)
      }
    }

    fetchPageAndGallery()
  }, [currentSlug, navigate])

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-regular fa-spinner-third animate-spin text-4xl text-brand-red" />
          <p className="text-sm font-semibold text-gray-500">Henter indhold...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <i className="fa-regular fa-triangle-exclamation text-5xl text-brand-red mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Der opstod en fejl</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg bg-brand-red px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-red-hover transition-colors cursor-pointer"
        >
          Prøv igen
        </button>
      </div>
    )
  }

  if (!pageData) return null

  // Check if page is Kontakt to render form
  const isKontaktPage = currentSlug === 'kontakt'
  const isEtableringsstoette = currentSlug === 'etableringsstoette'

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Hero Section */}
      <section className="relative flex min-h-[45vh] md:min-h-[55vh] lg:min-h-[60vh] items-center justify-center overflow-hidden bg-brand-dark text-white">
        {/* Hero Background Slideshow */}
        <div className="absolute inset-0">
          {heroImagesToUse.map((imgUrl, idx) => (
            <img
              key={imgUrl}
              src={getResizedImageUrl(imgUrl, 'width=1920,quality=80,format=auto')}
              alt={`${pageData.title} hero background ${idx + 1}`}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
                idx === currentHeroIndex ? 'opacity-50 z-10' : 'opacity-0 z-0'
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/85 via-brand-dark/55 to-transparent z-10" />
          <div className="absolute inset-0 bg-black/50 z-10" />
        </div>

        <div className="relative z-20 mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)' }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-red px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm" style={{ textShadow: 'none' }}>
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
              {getBadgeText()}
            </span>
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl font-display leading-tight text-white">
              {pageData.title}
            </h1>
            {pageData.meta_description && (
              <p className="text-lg text-gray-100 leading-relaxed max-w-2xl">
                {pageData.meta_description}
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <a
                href="tel:004540420286"
                className="flex items-center gap-2 whitespace-nowrap rounded-xl bg-brand-red px-6 py-4 text-sm font-bold text-white shadow-lg hover:bg-brand-red-hover transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
              >
                <i className="fa-regular fa-phone text-base" />
                Ring på +45 4042 0286
              </a>
              <Link
                to="/kontakt"
                className="flex items-center gap-2 rounded-xl bg-white/10 border border-white/20 px-6 py-4 text-sm font-bold text-white hover:bg-white/20 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
              >
                <i className="fa-regular fa-envelope text-base text-brand-red" />
                Få et uforpligtende tilbud
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-12">
        <div className="grid gap-10 lg:grid-cols-3">
          {/* Left/Middle: Content */}
          <div className="lg:col-span-2 space-y-8">
            {isEtableringsstoette ? (
              <div className="space-y-8">
                {/* Official EU Document Card */}
                <article className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-10 font-sans space-y-6">
                  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-5">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-widest text-[#2c3a8c]">
                        Officiel offentliggørelse
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-bold font-display text-gray-900 mt-1">
                        Sloth’s Maskinstation
                      </h2>
                    </div>
                    <div className="flex items-center gap-2 rounded-full bg-blue-50 px-3.5 py-1.5 text-xs font-semibold text-[#2c3a8c] border border-blue-100">
                      <i className="fa-solid fa-flag-checkered text-[11px]" />
                      Generationsskifte
                    </div>
                  </div>

                  <p className="text-[15px] sm:text-base leading-relaxed text-gray-800">
                    Sloth's Maskinstation har modtaget støtte fra Den Europæiske Union gennem ordningen Etableringsstøtte til unge landbrugere i forbindelse med generationsskiftet på slægtsgården.
                  </p>

                  <p className="text-[15px] sm:text-base leading-relaxed text-gray-800">
                    Formålet er at gennemføre et vellykket generationsskifte og samtidig sikre den fortsatte udvikling af planteavlsbedriften under Sloth's Maskinstation. Støtten bidrager til at skabe et solidt grundlag for fremtidig drift, vækst og bæredygtig udvikling af virksomheden.
                  </p>

                  <p className="text-[15px] sm:text-base leading-relaxed text-gray-800">
                    Som led i generationsskiftet har jeg overtaget og etableret mig som landbruger med fokus på planteavl. Gennem flere generationer har gården været drevet af familien, og med dette generationsskifte sikres det, at virksomheden fortsat kan skabe værdi for kunder, samarbejdspartnere og lokalsamfundet i mange år fremover.
                  </p>

                  <p className="text-[15px] sm:text-base leading-relaxed text-gray-800">
                    Generationsskiftet markerer et vigtigt skridt for Sloth's Maskinstation, og vi ser frem til at bygge videre på virksomhedens historie og fortsatte udvikling.
                  </p>

                  {/* Highlight Block with EU Emblem */}
                  <div className="rounded-xl bg-[#2c3a8c]/5 border border-[#2c3a8c]/20 p-5 sm:p-6 space-y-4">
                    <p className="text-[15px] sm:text-base font-bold text-gray-950 leading-snug">
                      Projektet er støttet af Den Europæiske Union gennem ordningen Etableringsstøtte til unge landbrugere.
                    </p>

                    {/* Official EU Lockup */}
                    <div className="flex items-center gap-4 pt-2">
                      <div className="w-16 h-11 bg-[#2c3a8c] rounded-sm flex items-center justify-center p-1 relative shrink-0 shadow-sm">
                        <svg className="w-8 h-8 text-[#ffcc00] fill-current" viewBox="0 0 100 100" aria-label="EU Flag">
                          <defs>
                            <polygon id="eu-star" points="0,-4 1.1,-1.2 4,-1.2 1.6,0.6 2.5,3.4 0,1.7 -2.5,3.4 -1.6,0.6 -4,-1.2 -1.1,-1.2" fill="#ffcc00" />
                          </defs>
                          <use href="#eu-star" transform="translate(50, 50) rotate(0) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(30) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(60) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(90) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(120) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(150) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(180) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(210) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(240) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(270) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(300) translate(0, -32)" />
                          <use href="#eu-star" transform="translate(50, 50) rotate(330) translate(0, -32)" />
                        </svg>
                      </div>

                      <div className="flex flex-col justify-center text-[#2c3a8c] font-bold tracking-tight leading-[1.15]">
                        <span className="text-xs uppercase tracking-wider text-[#2c3a8c]/80">Finansieret af</span>
                        <span className="text-base sm:text-lg">Den Europæiske Union</span>
                      </div>
                    </div>
                  </div>

                  {/* Metadata Hashtags */}
                  <footer className="pt-3 border-t border-gray-100 flex flex-wrap gap-2 text-xs text-gray-500 font-medium">
                    <span>#FinansieretAfEU</span>
                    <span>#DenEuropæiskeUnion</span>
                    <span>#Etableringsstøtte</span>
                    <span>#Generationsskifte</span>
                    <span>#SlothsMaskinstation</span>
                    <span>#Planteavl</span>
                    <span>#Landbrug</span>
                  </footer>
                </article>

                {/* Document Scan Showcase */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 border-b border-gray-100 pb-4">
                    <div>
                      <h3 className="text-xl font-bold font-display text-gray-900">
                        Dokumentation og støtteerklæring
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Klik på dokumentet for at forstørre eller åbne i fuld opløsning.
                      </p>
                    </div>
                    <a
                      href="https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/13509.jpg"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer w-fit"
                    >
                      <i className="fa-regular fa-arrow-up-right-from-square text-[11px]" />
                      Åbn original fil
                    </a>
                  </div>

                  <div
                    onClick={() => setLightboxIndex(0)}
                    className="group relative cursor-pointer overflow-hidden rounded-xl border border-gray-200 bg-gray-50 max-w-xl mx-auto shadow-xs hover:shadow-md transition-shadow"
                  >
                    <img
                      src={getResizedImageUrl('https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/13509.jpg', 'width=800,quality=90,format=auto')}
                      alt="Sloth's Maskinstation - Finansieret af Den Europæiske Union - Etableringsstøtte"
                      className="w-full h-auto object-contain transition-transform duration-300 group-hover:scale-[1.01]"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <span className="rounded-lg bg-white/90 px-3 py-1.5 text-xs font-bold text-gray-900 shadow-md backdrop-blur-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5">
                        <i className="fa-regular fa-magnifying-glass-plus" />
                        Klik for fuld skærm
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm md:p-8">
                {/* Render HTML content safely */}
                <div
                  className="prose prose-brand max-w-none text-gray-700 leading-relaxed space-y-6 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:font-display [&_h1]:text-gray-900 [&_h1]:mt-6 [&_h1]:mb-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:font-display [&_h2]:text-gray-900 [&_h2]:mt-6 [&_h2]:mb-4 [&_a]:text-brand-red [&_a]:hover:underline [&_a]:font-semibold [&_a[href^='tel:']]:whitespace-nowrap"
                  dangerouslySetInnerHTML={{ 
                    __html: pageData.content ? pageData.content.replace(/^<h1>.*?<\/h1>/i, '') : '' 
                  }}
                />

                {/* Subpage banner for Om os */}
                {currentSlug === 'om-os-galleri' && (
                  <div className="mt-8 rounded-xl bg-blue-50/70 border border-blue-100 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2c3a8c] uppercase tracking-wider">
                        <i className="fa-solid fa-award" />
                        Generationsskifte & EU-støtte
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        Læs om vores generationsskifte og modtagelsen af Etableringsstøtte til unge landbrugere.
                      </p>
                    </div>
                    <Link
                      to="/om-os-galleri/etableringsstoette"
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-[#2c3a8c] px-4 py-2.5 text-xs font-bold text-white hover:bg-[#232e70] transition-colors shadow-xs"
                    >
                      Læs mere her
                      <i className="fa-regular fa-arrow-right text-[11px]" />
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Gallery Section (Technical Reference) */}
            {galleryData && galleryData.images && galleryData.images.length > 0 && (
              <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm md:p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 font-display border-l-4 border-brand-red pl-3">
                  Billedgalleri
                </h3>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                  {galleryData.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setLightboxIndex(idx)}
                      className="group relative aspect-square overflow-hidden rounded-xl bg-gray-100 border border-gray-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-red"
                    >
                      <img
                        src={getResizedImageUrl(img, 'width=300,height=300,fit=cover,quality=85,format=auto')}
                        alt={`Galleri ${idx + 1}`}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <i className="fa-regular fa-magnifying-glass-plus text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Sidebar */}
          <div className="space-y-8">
            {/* Contact Form on Kontakt page */}
            {isKontaktPage && (
              <ContactForm />
            )}

            {/* Subpages navigation under Om os & Galleri */}
            {(currentSlug === 'om-os-galleri' || currentSlug === 'historie' || isEtableringsstoette) && (
              <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm space-y-4">
                <h3 className="text-lg font-bold text-gray-900 font-display border-l-4 border-brand-red pl-3">
                  Om os & Galleri
                </h3>
                <nav className="space-y-1.5 text-sm">
                  <Link
                    to="/om-os-galleri"
                    className={`flex items-center justify-between rounded-lg px-3.5 py-2.5 font-semibold transition-colors ${
                      currentSlug === 'om-os-galleri'
                        ? 'bg-brand-red text-white'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
                    }`}
                  >
                    <span>Om virksomheden</span>
                    <i className="fa-regular fa-chevron-right text-xs" />
                  </Link>
                  <Link
                    to="/om-os-galleri/historie"
                    className={`flex items-center justify-between rounded-lg px-3.5 py-2.5 font-semibold transition-colors ${
                      currentSlug === 'historie'
                        ? 'bg-brand-red text-white'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
                    }`}
                  >
                    <span>Historie</span>
                    <i className="fa-regular fa-chevron-right text-xs" />
                  </Link>
                  <Link
                    to="/maskinudlejning-salg/maskinpark"
                    className="flex items-center justify-between rounded-lg px-3.5 py-2.5 font-semibold text-gray-700 hover:bg-gray-50 hover:text-brand-red transition-colors"
                  >
                    <span>Maskinpark</span>
                    <i className="fa-regular fa-chevron-right text-xs" />
                  </Link>
                  <Link
                    to="/om-os-galleri/etableringsstoette"
                    className={`flex items-center justify-between rounded-lg px-3.5 py-2.5 font-semibold transition-colors ${
                      isEtableringsstoette
                        ? 'bg-brand-red text-white'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
                    }`}
                  >
                    <span>Etableringsstøtte</span>
                    <i className="fa-regular fa-chevron-right text-xs" />
                  </Link>
                </nav>
              </div>
            )}

            {/* EU Info Sidebar Card on Etableringsstøtte */}
            {isEtableringsstoette && (
              <div className="rounded-2xl border border-blue-100 bg-white p-6 shadow-sm space-y-4">
                <div className="flex items-center gap-2.5 text-[#2c3a8c]">
                  <i className="fa-solid fa-circle-info text-lg" />
                  <h4 className="font-bold text-gray-900 font-display">
                    Om støtteordningen
                  </h4>
                </div>
                <p className="text-xs leading-relaxed text-gray-600">
                  Etableringsstøtte til unge landbrugere er en EU-ordning, der støtter generationsskifter og etablering i landbruget for at sikre fremtidens bæredygtige fødevareproduktion og levedygtige landdistrikter.
                </p>
                <div className="pt-2 border-t border-gray-100 space-y-2 text-xs">
                  <div className="flex justify-between py-1 border-b border-gray-50">
                    <span className="text-gray-500">Område:</span>
                    <span className="font-bold text-gray-800">Planteavl</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-50">
                    <span className="text-gray-500">Formål:</span>
                    <span className="font-bold text-gray-800">Generationsskifte</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-50">
                    <span className="text-gray-500">Støttegiver:</span>
                    <span className="font-bold text-[#2c3a8c]">Den Europæiske Union</span>
                  </div>
                </div>
              </div>
            )}

            {/* Brochure download box */}
            <div className="rounded-2xl border border-gray-100 bg-brand-dark p-6 text-white shadow-sm space-y-4 relative overflow-hidden">
              <div className="absolute -right-10 -bottom-10 h-32 w-32 rounded-full bg-brand-red/10 blur-xl" />
              <h3 className="text-xl font-bold text-white font-display border-l-4 border-brand-red pl-3">
                Hent vores brochure
              </h3>
              <p className="text-xs text-gray-400 leading-relaxed">
                Læs mere om vores ydelser og maskiner i vores officielle brochure fra 2019.
              </p>
              <a
                href="https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/slothsmaskinstationbrochure2019-a4-8sider-23okt-1.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-lg bg-brand-red px-4 py-3 text-sm font-bold text-white hover:bg-brand-red-hover transition-colors w-full cursor-pointer"
              >
                <i className="fa-regular fa-file-pdf text-base" />
                Hent PDF Brochure
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <GalleryLightbox
          images={
            isEtableringsstoette && !galleryData?.images?.length
              ? ['https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/13509.jpg']
              : galleryData?.images || []
          }
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  )
}
